/**
 * AI Agents API service
 */
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/api/axiosInstance';
import {
  Agent,
  AgentCreate,
  AgentUpdate,
  AvatarIdentity,
  VoiceIdentity,
  ContextFileAssociation,
  PaginatedResponse,
  DataResponse,
  ListAgentsParams,
  VirtualUser,
  AgentChat,
  ListChatsParams,
  PromptConfigResponse,
  AgentTemplate,
  AgentTemplateCreate,
  AgentTemplateUpdate,
  AgentTemplateGalleryResponse,
  ListTemplatesParams,
  SaveAsTemplateRequest,
  AgentScaffoldRequest,
  AgentScaffoldDraft,
} from '../types';
import type { AiAgentsDashboard } from '../../../generated/api/models/aiAgentsDashboard';

const API_BASE = '/api/v1/ai-agents';

export const agentApi = {
  // Agents
  listAgents: async (params?: ListAgentsParams): Promise<PaginatedResponse<Agent>> => {
    const searchParams = new URLSearchParams();
    if (params?.is_active !== undefined) searchParams.set('is_active', String(params.is_active));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/agents${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Agent>>(url);
  },

  getAgent: async (id: string, includeFiles?: boolean): Promise<DataResponse<Agent>> => {
    const url = includeFiles
      ? `${API_BASE}/agents/${id}?include_files=true`
      : `${API_BASE}/agents/${id}`;
    return apiGet<DataResponse<Agent>>(url);
  },

  createAgent: async (data: AgentCreate): Promise<DataResponse<Agent>> => {
    return apiPost<DataResponse<Agent>>(`${API_BASE}/agents`, data);
  },

  // Prompt-to-scaffold: generate a reviewable OCTOPUS draft from a description.
  // Persists nothing — the draft is created only when the admin confirms.
  scaffoldAgent: async (
    data: AgentScaffoldRequest
  ): Promise<DataResponse<AgentScaffoldDraft>> => {
    return apiPost<DataResponse<AgentScaffoldDraft>>(`${API_BASE}/agents/scaffold`, data);
  },

  updateAgent: async (id: string, data: AgentUpdate): Promise<DataResponse<Agent>> => {
    return apiPut<DataResponse<Agent>>(`${API_BASE}/agents/${id}`, data);
  },

  deleteAgent: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/agents/${id}`);
  },

  reorderAgent: async (id: string, position: number): Promise<DataResponse<Agent>> => {
    return apiPut<DataResponse<Agent>>(`${API_BASE}/agents/${id}/reorder`, {
      position,
    });
  },

  // Context Files
  listContextFiles: async (agentId: string): Promise<DataResponse<ContextFileAssociation[]>> => {
    return apiGet<DataResponse<ContextFileAssociation[]>>(`${API_BASE}/agents/${agentId}/context-files`);
  },

  getFileSystemStats: async (agentId: string): Promise<DataResponse<{ file_count: number; folder_count: number }>> => {
    return apiGet<DataResponse<{ file_count: number; folder_count: number }>>(`${API_BASE}/agents/${agentId}/file-system-stats`);
  },

  addContextFile: async (agentId: string, fileId: string): Promise<DataResponse<ContextFileAssociation>> => {
    return apiPost<DataResponse<ContextFileAssociation>>(`${API_BASE}/agents/${agentId}/context-files`, {
      file_id: fileId,
    });
  },

  addContextFolder: async (agentId: string, folderId: string): Promise<DataResponse<ContextFileAssociation>> => {
    return apiPost<DataResponse<ContextFileAssociation>>(`${API_BASE}/agents/${agentId}/context-files`, {
      folder_id: folderId,
    });
  },

  removeContextFile: async (agentId: string, associationId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/agents/${agentId}/context-files/${associationId}`);
  },

  // Avatars
  listAvatars: async (): Promise<DataResponse<AvatarIdentity[]>> => {
    return apiGet<DataResponse<AvatarIdentity[]>>(`${API_BASE}/avatars`);
  },

  // Voices
  listVoices: async (): Promise<DataResponse<VoiceIdentity[]>> => {
    return apiGet<DataResponse<VoiceIdentity[]>>(`${API_BASE}/voices`);
  },

  // Virtual Users (users with user_type='ai_agent')
  listVirtualUsers: async (): Promise<DataResponse<VirtualUser[]>> => {
    return apiGet<DataResponse<VirtualUser[]>>(`${API_BASE}/virtual-users`);
  },

  getVirtualUser: async (id: string): Promise<DataResponse<VirtualUser>> => {
    return apiGet<DataResponse<VirtualUser>>(`${API_BASE}/virtual-users/${id}`);
  },

  listAccessibleVirtualUsers: async (): Promise<DataResponse<VirtualUser[]>> => {
    return apiGet<DataResponse<VirtualUser[]>>(`${API_BASE}/virtual-users/accessible`);
  },

  validateActingAsAccess: async (virtualUserId: string): Promise<DataResponse<{ virtual_user: VirtualUser; can_act_as: boolean }>> => {
    return apiGet<DataResponse<{ virtual_user: VirtualUser; can_act_as: boolean }>>(`${API_BASE}/virtual-users/${virtualUserId}/validate-access`);
  },

  // Chat Sessions
  listAgentChats: async (agentId: string, params?: ListChatsParams): Promise<DataResponse<AgentChat[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/agents/${agentId}/chats${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<DataResponse<AgentChat[]>>(url);
  },

  getAgentChat: async (agentId: string, chatId: string): Promise<DataResponse<AgentChat>> => {
    return apiGet<DataResponse<AgentChat>>(`${API_BASE}/agents/${agentId}/chats/${chatId}`);
  },

  createAgentChat: async (agentId: string): Promise<DataResponse<AgentChat>> => {
    return apiPost<DataResponse<AgentChat>>(`${API_BASE}/agents/${agentId}/chats`);
  },

  sendMessageToChat: async (
    agentId: string,
    chatId: string,
    message: string
  ): Promise<DataResponse<{ response: string; chat: AgentChat }>> => {
    return apiPost<DataResponse<{ response: string; chat: AgentChat }>>(`${API_BASE}/agents/${agentId}/chats/${chatId}/messages`, {
      message,
    });
  },

  endAgentChat: async (agentId: string, chatId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/agents/${agentId}/chats/${chatId}`);
  },

  // System Status
  getStatus: async (): Promise<DataResponse<{ mcp_tools_enabled: boolean; is_local_mode: boolean }>> => {
    return apiGet<DataResponse<{ mcp_tools_enabled: boolean; is_local_mode: boolean }>>(`${API_BASE}/status`);
  },

  // Prompt Configuration
  getPromptConfig: async (): Promise<DataResponse<PromptConfigResponse>> => {
    return apiGet<DataResponse<PromptConfigResponse>>(`${API_BASE}/prompt-config`);
  },

  // ============================================================================
  // Agent Templates
  // ============================================================================

  // List templates with optional filters
  listTemplates: async (params?: ListTemplatesParams): Promise<PaginatedResponse<AgentTemplate>> => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.include_system !== undefined) searchParams.set('include_system', String(params.include_system));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/templates${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<AgentTemplate>>(url);
  },

  // Get template gallery with sections
  getTemplateGallery: async (): Promise<AgentTemplateGalleryResponse> => {
    return apiGet<AgentTemplateGalleryResponse>(`${API_BASE}/templates/gallery`);
  },

  // Get template categories
  getTemplateCategories: async (): Promise<DataResponse<string[]>> => {
    return apiGet<DataResponse<string[]>>(`${API_BASE}/templates/categories`);
  },

  // Get single template
  getTemplate: async (id: string): Promise<DataResponse<AgentTemplate>> => {
    return apiGet<DataResponse<AgentTemplate>>(`${API_BASE}/templates/${id}`);
  },

  // Save agent as template
  saveAsTemplate: async (agentId: string, data: SaveAsTemplateRequest): Promise<DataResponse<AgentTemplate>> => {
    return apiPost<DataResponse<AgentTemplate>>(`${API_BASE}/templates/from-agent/${agentId}`, data);
  },

  // Admin: Create tenant template
  createTemplate: async (data: AgentTemplateCreate): Promise<DataResponse<AgentTemplate>> => {
    return apiPost<DataResponse<AgentTemplate>>(`${API_BASE}/admin/templates`, data);
  },

  // Admin: Update tenant template
  updateTemplate: async (id: string, data: AgentTemplateUpdate): Promise<DataResponse<AgentTemplate>> => {
    return apiPut<DataResponse<AgentTemplate>>(`${API_BASE}/admin/templates/${id}`, data);
  },

  // Admin: Delete tenant template
  deleteTemplate: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/admin/templates/${id}`);
  },

  // Admin: Suppress system template
  suppressTemplate: async (id: string): Promise<void> => {
    await apiPost(`${API_BASE}/admin/templates/suppress/${id}`);
  },

  // Admin: Unsuppress system template
  unsuppressTemplate: async (id: string): Promise<void> => {
    await apiPost(`${API_BASE}/admin/templates/unsuppress/${id}`);
  },
};

// Dashboard widgets — single aggregate read feeding the AI Agents widgets.
// Schema-first: the endpoint returns the bare AiAgentsDashboard model (no {data}
// envelope) via the shared KPI substrate. See docs/platform/dashboard-kpi-endpoints.md.
export const agentDashboardApi = {
  get: (): Promise<AiAgentsDashboard> => apiGet<AiAgentsDashboard>(`${API_BASE}/dashboard/stats`),
};
